import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import type { QueryClient } from "@tanstack/react-query";
import {
  abrirSessao,
  aceitarConvite,
  buscarSessao,
  encerrarSessao,
  ErroDaApi,
  redefinirSenha,
  type DesafioDeMfa,
  type Sessao,
} from "./api";

export const CHAVE_SESSAO = ["sessao"] as const;

/**
 * Quem está nesta aba do navegador. `undefined` enquanto a resposta não chegou,
 * `null` quando não há sessão.
 *
 * `retry: false` é deliberado: a ausência de sessão não é falha e insistir só atrasa o
 * envio para a tela de entrada.
 */
export function useSessao() {
  return useQuery<Sessao | null>({
    queryKey: CHAVE_SESSAO,
    queryFn: ({ signal }) => buscarSessao({ signal }),
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * Busca a sessão e guarda no cache — usado logo depois de entrar, para decidir o destino.
 *
 * Cancela antes a busca que estiver em voo. A tela de entrada já pergunta `/sessao/eu` ao
 * abrir, SEM cookie; quem entra antes dessa resposta chegar (preenchimento automático + Enter)
 * teria o `fetchQuery` reaproveitando aquela requisição anterior ao login — ela volta 401, e a
 * pessoa era devolvida para `/entrar` com a sessão já criada no servidor.
 */
export async function recarregarSessao(cliente: QueryClient): Promise<Sessao | null> {
  await cliente.cancelQueries({ queryKey: CHAVE_SESSAO });
  return cliente.fetchQuery({
    queryKey: CHAVE_SESSAO,
    queryFn: ({ signal }) => buscarSessao({ signal }),
    staleTime: 0,
    retry: false,
  });
}

/** Usuário sem organização é interno — é o que o modelo de dados define. */
export function ehInterno(sessao: Sessao): boolean {
  return sessao.usuario.interno ?? sessao.organizacao === null;
}

/**
 * Se a sessão declara uma permissão.
 *
 * Serve para a tela **não oferecer** o que o servidor vai negar — um botão "Revelar" que sempre
 * devolve 403 ensina a pessoa a ignorar recusa. Não serve para proteger nada: quem nega é o
 * serviço, que tem a sessão de banco, a política de linha e a linha de auditoria. O ADR 0006 é
 * literal a respeito — *"tela é encaminhamento"* —, e é por isso que esconder o botão nunca
 * substitui o teste que prova a negativa contra o servidor.
 */
export function podeNaSessao(sessao: Sessao | null | undefined, permissao: string): boolean {
  return sessao?.permissoes.includes(permissao) ?? false;
}

/** Rótulo curto do lado direito da topbar: a organização, ou o papel de quem é interno. */
export function descricaoDoUsuario(sessao: Sessao): string {
  if (sessao.organizacao?.nome) return sessao.organizacao.nome;
  const papel = sessao.usuario.papeis?.[0];
  if (papel) return papel;
  return ehInterno(sessao) ? "GEOvendas" : "organização não identificada";
}

/** Para onde a pessoa vai depois de entrar, quando não veio de uma rota específica. */
export function destinoPadrao(sessao: Sessao | null): string {
  if (!sessao) return "/entrar";
  return ehInterno(sessao) ? "/admin" : "/app";
}

/**
 * Mensagem de falha de autenticação.
 *
 * `401` é sempre a mesma frase, sem dizer se o e-mail existe: a tela de entrada não pode
 * virar um verificador de contas do portal.
 */
export function mensagemDeEntrada(erro: unknown): string {
  if (erro instanceof ErroDaApi) {
    // A política de rede da organização: a recusa só chega a quem já provou a senha, e a frase
    // é escrita para a pessoa saber o que fazer ("acesse pela rede da empresa").
    if (erro.codigo === "ip_nao_permitido") return erro.message;
    if (erro.status === 401 || erro.status === 400) {
      return "E-mail ou senha inválidos.";
    }
    if (erro.status === 429) {
      return "Muitas tentativas seguidas. Espere alguns minutos e tente de novo.";
    }
    if (erro.status === null) {
      return "Não foi possível falar com o portal. Verifique a conexão e tente de novo.";
    }
  }
  return "Não foi possível entrar agora. Tente de novo em instantes.";
}

/** O que entrar (ou aceitar um convite) devolve: a sessão, ou o desafio do segundo fator. */
export type ResultadoDaEntrada = Sessao | null | DesafioDeMfa;

/** `true` quando a senha conferiu mas falta o segundo fator — nenhuma sessão foi aberta. */
export function ehDesafio(resultado: ResultadoDaEntrada): resultado is DesafioDeMfa {
  return resultado !== null && typeof resultado === "object" && "desafio" in resultado;
}

export function useEntrar() {
  const cliente = useQueryClient();
  return useMutation<ResultadoDaEntrada, Error, { email: string; senha: string }>({
    mutationFn: async (credenciais) => {
      const desafio = await abrirSessao(credenciais.email, credenciais.senha);
      return desafio ?? recarregarSessao(cliente);
    },
  });
}

export function useAceitarConvite(token: string) {
  const cliente = useQueryClient();
  return useMutation<ResultadoDaEntrada, Error, string>({
    mutationFn: async (senha) => {
      const desafio = await aceitarConvite(token, senha);
      return desafio ?? recarregarSessao(cliente);
    },
  });
}

/** Depois do segundo fator o cookie já existe: só falta carregar quem entrou. */
export function useConcluirEntrada() {
  const cliente = useQueryClient();
  return useMutation({ mutationFn: () => recarregarSessao(cliente) });
}

export function useRedefinirSenha(token: string) {
  return useMutation({
    mutationFn: (senha: string) => redefinirSenha(token, senha),
  });
}

export function useSair() {
  const cliente = useQueryClient();
  const navegar = useNavigate();
  return useMutation({
    // Consulta em voo que chegasse ao servidor depois do `DELETE /sessao` voltaria 401 — ruído
    // no console e, pior, um erro de tela para uma pessoa que só clicou em Sair.
    onMutate: () => cliente.cancelQueries(),
    mutationFn: encerrarSessao,
    // O cache é limpo mesmo quando a chamada falha: manter na tela uma sessão que a pessoa
    // mandou encerrar é pior do que uma sessão órfã no servidor, que expira sozinha.
    onSettled: () => {
      // Defeito 3.1 — sair encerra POR COMPLETO. A ordem importa: primeiro sair da rota
      // protegida, sem `de`, e só então esvaziar o cache. Na ordem inversa o guarda de rota vê
      // a sessão nula ANTES e redireciona com `{ de: <área atual> }`, e a próxima pessoa que
      // entrar na aba é mandada para a área da anterior. E o cache inteiro, não só a chave da
      // sessão: dado de cliente não pode sobreviver para o perfil seguinte.
      navegar("/entrar", { replace: true });
      cliente.clear();
      cliente.setQueryData(CHAVE_SESSAO, null);
    },
  });
}
